import type * as React from 'react';

import * as icons from './icons/index';
import { ukIcons } from './icons-uk/registry';
import { type IconSet, useIconSet } from './iconSet';

// `height`/`width` stay as wide as SVGProps<SVGSVGElement>: narrowing them to `number` makes the two
// icon sets structurally incompatible.
interface IconAttributes {
  accent?: string;
  className?: string;
  fill?: string;
  height?: number | string;
  preserveAspectRatio?: string;
  primary?: string;
  width?: number | string;
}

export interface IconProps extends IconAttributes {
  icon: string;
  /** Overrides the enclosing IconSetProvider. Prefer the provider; this is an escape hatch. */
  iconSet?: IconSet;
  /**
   * Name to use instead of `icon` when the UK set is active. For a component that renders in both
   * chromes and needs a different glyph in each — the UK set is keyed by Figma name, so there is no
   * name a shared call site could pass that resolves correctly in both.
   */
  ukIcon?: string;
}

// Declared locally, and as possibly-undefined, because this module is compiled into browser bundles by
// consumers whose tsconfig has no node types.
declare const process: { env: { NODE_ENV?: string } } | undefined;

const warnedIcons = new Set<string>();

// Absent `process` must count as production, not as dev — the operand order below is load-bearing.
// See tests/warnMissingIcon.test.ts.
const warnMissingIcon = (icon: string) => {
  const isProduction = typeof process === 'undefined' || process.env.NODE_ENV === 'production';
  if (isProduction || warnedIcons.has(icon)) return;

  warnedIcons.add(icon);
  console.warn(`Missing icon: ${icon}`);
};

// Own-property only: a CMS icon name of `constructor` must not resolve to an inherited member and be
// handed to React as a component. Applied to BOTH sets — the legacy one is a module namespace object,
// but bundlers synthesize that object and none of them guarantees a null prototype.
// See tests/ukIcons.test.ts.
const pick = <T,>(set: Record<string, T>, name: string): T | undefined =>
  Object.hasOwn(set, name) ? set[name] : undefined;

const legacyIcons = icons as unknown as Record<string, React.ComponentType<IconAttributes>>;

// The UK set OVERLAYS the legacy one rather than replacing it, so a CMS name with no UK counterpart
// keeps its legacy glyph instead of blanking. See icon.iconSet.spec.tsx.
const lookup = (name: string, activeIconSet: IconSet) =>
  (activeIconSet === 'uk' ? pick(ukIcons, name) : undefined) ?? pick(legacyIcons, name);

/** Hook-free so it can run without a React renderer — see tests/warnMissingIcon.test.ts. */
export const resolveIcon = (
  icon: string,
  activeIconSet: IconSet = 'legacy',
  ukIcon?: string,
): React.ComponentType<IconAttributes> | undefined => {
  if (!icon) return undefined;

  // The `ukIcon` attempt is UK-set-only, NOT `lookup`. Eighteen names exist in both sets, so going
  // through `lookup` would let a missing UK entry land on the legacy glyph of the ukIcon name instead
  // of the `icon` the call site nominated as its fallback. See icon.iconSet.spec.tsx.
  const component =
    (activeIconSet === 'uk' && ukIcon ? pick(ukIcons, ukIcon) : undefined) ?? lookup(icon, activeIconSet);
  if (!component) warnMissingIcon(activeIconSet === 'uk' && ukIcon ? ukIcon : icon);

  return component;
};

const Icon = ({ icon, iconSet, ukIcon, ...iconProps }: IconProps) => {
  const contextIconSet = useIconSet();
  const Component = resolveIcon(icon, iconSet ?? contextIconSet, ukIcon);

  if (!Component) return null;

  // `resolveIcon` returns an existing module-level component, it does not create one — the rule only
  // sees a call expression feeding JSX.
  // oxlint-disable-next-line react/static-components
  return <Component {...iconProps} />;
};

export type IconType = typeof Icon;

export default Icon;
